import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import PageBanner from "../components/PageBanner";

const EFFECTIVE_DATE = "16 October 2026";

/*
 * Terms content.
 * Each section has an id (used for the side menu / anchor links) and can have:
 * paragraphs [], list [], closing paragraphs (after) [], and highlight (true = blue callout box).
 * Paragraphs can be plain strings or JSX (for inline links).
 */
const sections = [
  {
    id: "about",
    title: "1. About Konnect Diagnostics",
    paragraphs: [
      "Konnect Diagnostics provides an online platform through which users may view diagnostic service information and submit booking or service requests. Tests, sample collection, processing, and reporting may be performed by Konnect Diagnostics or a participating laboratory, collection partner, or healthcare service provider, depending on the service selected.",
    ],
  },
  {
    id: "eligibility",
    title: "2. Eligibility and Account Registration",
    paragraphs: [
      "You must provide accurate, complete, and current information when creating an account or making a booking. You are responsible for protecting your login credentials and for activity carried out through your account. Notify us promptly if you suspect unauthorised access.",
      "If you book for another person, you confirm that you are authorised to provide their details and make the booking on their behalf.",
    ],
  },
  {
    id: "bookings",
    title: "3. Bookings and Appointments",
    paragraphs: [
      "A booking submitted through the website is a request and may require confirmation. Availability depends on service-area coverage, partner capacity, preparation requirements, and operational constraints. Appointment times are estimates and may change due to traffic, weather, staffing, laboratory operations, or other circumstances. We may contact you to confirm details or offer an alternative appointment.",
      "Provide a working phone number, accurate address, and all information reasonably required to fulfil the booking. Review the test or package, patient details, location, date, and time before submitting your request.",
    ],
  },
  {
    id: "medical-disclaimer",
    title: "4. Test Information and Medical Disclaimer",
    highlight: true,
    paragraphs: [
      "Website information is for general informational purposes and is not a substitute for medical advice, diagnosis, or treatment from a qualified healthcare professional. Test selection, interpretation of results, and treatment decisions should be discussed with an appropriately qualified clinician.",
      "Availability, descriptions, preparation instructions, reference ranges, turnaround times, and prices may vary by location, laboratory, testing method, patient factors, or clinical requirements. Follow instructions provided for your specific test. Do not delay or disregard medical advice because of information obtained through the website or a test result.",
    ],
  },
  {
    id: "sample-collection",
    title: "5. Sample Collection and Patient Responsibilities",
    paragraphs: [
      "Follow applicable preparation instructions. Follow fasting or medication instructions only where provided by a qualified healthcare professional or the relevant laboratory. Tell the collection team any relevant information needed for safe collection. Collection may be postponed or declined if the patient is unavailable, the location is inaccessible, required information is missing, or collection cannot safely be completed.",
      "A sample may be unsuitable, insufficient, damaged, delayed, or require recollection. The laboratory or service provider may contact you about next steps; some tests may require additional samples or confirmation.",
    ],
  },
  {
    id: "prices-payments",
    title: "6. Prices and Payments",
    paragraphs: [
      "Prices displayed at the time of booking apply, subject to confirmation and correction of obvious errors. Applicable taxes, collection charges, convenience fees, or other disclosed charges may apply. Payments may be processed by a third-party payment provider under its terms. We do not intend to store full payment-card details on the website.",
      "A payment attempt does not necessarily mean a booking is confirmed. If money is debited but your booking is not confirmed, contact customer care with the transaction details. Refund timing may depend on the payment provider and banking network.",
    ],
  },
  {
    id: "cancellation-refunds",
    title: "7. Cancellation, Rescheduling, and Refunds",
    paragraphs: [
      "Cancellation, rescheduling, and refund eligibility depend on the service booked, whether collection or testing has begun, partner policies, and any terms disclosed during booking. Submit requests through the website or customer care as early as possible. Services already performed, samples already collected, or tests already processed may not be refundable, except where required by law or stated in the booking-specific policy.",
      "If Konnect Diagnostics or a service partner cancels a confirmed booking or cannot provide the service, we will communicate available options, which may include rescheduling or a refund where applicable.",
    ],
  },
  {
    id: "reports",
    title: "8. Reports and Result Availability",
    paragraphs: [
      "Reports are delivered using the method communicated for the service, which may include an online account, email, or another verified channel. Turnaround times are estimates and may be affected by test complexity, repeat testing, quality checks, equipment issues, or operational factors.",
      "Keep your account and contact details secure. If a report appears incorrect or belongs to the wrong person, contact customer care promptly and do not share it publicly. Reports should be interpreted by a qualified healthcare professional in the context of symptoms, medical history, and other clinical information.",
    ],
  },
  {
    id: "privacy",
    title: "9. Privacy and Personal Information",
    paragraphs: [
      <>
        Use of the Services is also governed by the Konnect Diagnostics{" "}
        <Link to="/privacy-policy">Privacy Policy</Link>, which explains how
        personal information and, where applicable, health-related information
        is collected, used, stored, and shared. Please read that policy before
        using the website.
      </>,
      "When submitting information for another person, you confirm that you are entitled to provide it for the relevant booking or request.",
    ],
  },
  {
    id: "acceptable-use",
    title: "10. Acceptable Use",
    list: [
      "Use the website only for lawful purposes and do not provide false identity, contact, patient, or booking information.",
      "Do not access another person's account or report without authorisation.",
      "Do not interfere with the website, bypass security controls, introduce malicious code, or disrupt the Services.",
      "Do not copy, scrape, resell, or commercially exploit website content except as permitted by law or with our written permission.",
      "Do not use the website in a way that infringes another person's rights or violates applicable law.",
    ],
  },
  {
    id: "availability",
    title: "11. Website Availability and Third-Party Services",
    paragraphs: [
      "We aim to keep the website available and information accurate, but do not guarantee uninterrupted or error-free access. The website may be unavailable for maintenance, upgrades, technical issues, or events outside our reasonable control. Third-party links are provided for convenience; we are not responsible for their content, availability, or privacy practices.",
    ],
  },
  {
    id: "intellectual-property",
    title: "12. Intellectual Property",
    paragraphs: [
      "Unless otherwise stated, website content, branding, text, graphics, design, and other materials are owned by or licensed to Konnect Diagnostics and protected by applicable intellectual-property laws. You may use the website for personal, non-commercial purposes. No ownership rights are transferred through your use of the Services.",
    ],
  },
  {
    id: "liability",
    title: "13. Limitation of Liability",
    paragraphs: [
      "To the extent permitted by applicable law, Konnect Diagnostics will not be liable for indirect, incidental, special, or consequential losses arising from use of the website or service delays, including losses caused by inaccurate information supplied by users, third-party interruptions, or events outside our reasonable control. Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited, or affects rights under applicable consumer-protection law.",
      "The website is a booking and information channel and does not replace professional medical care. Clinical decisions should be made with a qualified healthcare professional using the full clinical context.",
    ],
  },
  {
    id: "indemnity",
    title: "14. Indemnity",
    paragraphs: [
      "To the extent permitted by law, you are responsible for losses or claims arising directly from your unlawful use of the website, material breach of these Terms, or infringement of another person's rights.",
    ],
  },
  {
    id: "suspension",
    title: "15. Suspension or Termination",
    paragraphs: [
      "We may restrict or suspend access to an account or the Services where reasonably necessary to protect users, investigate suspected fraud or misuse, maintain security, comply with law, or address a material breach of these Terms. Where appropriate and legally permitted, we will provide notice or an opportunity to resolve the issue.",
    ],
  },
  {
    id: "changes",
    title: "16. Changes to These Terms",
    paragraphs: [
      "We may update these Terms to reflect changes in the Services, operations, or applicable law. The revised version will be posted on the website with an updated effective date. Continued use after revised Terms are posted indicates acceptance to the extent permitted by law.",
    ],
  },
  {
    id: "governing-law",
    title: "17. Governing Law and Disputes",
    paragraphs: [
      "These Terms are governed by the laws of India. Subject to mandatory rights and jurisdiction rules under applicable law, disputes arising from these Terms will be subject to the jurisdiction of competent courts in Hyderabad, Telangana, India.",
    ],
  },
];

const CONTACT_ID = "contact-us";
const menuItems = [
  ...sections.map(({ id, title }) => ({ id, title })),
  { id: CONTACT_ID, title: "18. Contact Us" },
];

function TermsAndConditions() {
  const [activeId, setActiveId] = useState(menuItems[0].id);

  // Highlight the section currently in view in the side menu
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );
    menuItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Wrapper className="terms-conditions">
      {/* Swap bannerImg for a dedicated terms banner class once one is added */}
      <PageBanner
        title={"Terms & Conditions"}
        bannerImg={"contact-us-bg-image"}
      />

      <div className="container px-md-0 px-4 mx-auto my-5">
        <div className="row g-4">
          {/* Side menu (desktop only) */}
          <aside className="col-lg-3 d-none d-lg-block">
            <nav className="toc bg_light2 shadow-sm rounded p-4">
              <h6 className="text_accent fw-bold mb-3">On this page</h6>
              <ul className="list-unstyled m-0">
                {menuItems.map(({ id, title }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      onClick={(e) => scrollTo(e, id)}
                      className={activeId === id ? "active" : ""}
                    >
                      {title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Terms content */}
          <div className="col-lg-9">
            <div className="terms-card rounded p-md-5 p-4">
              <p className="dates text-k-text mb-4">
                <span className="fw-bold">Effective Date:</span>{" "}
                {EFFECTIVE_DATE}
              </p>

              <p className="intro text-k-text">
                These Terms & Conditions ("Terms") govern your access to and use
                of the Konnect Diagnostics website, including account
                registration, diagnostic test and package information,
                appointment bookings, home sample collection requests, payments,
                and related services ("Services"). By accessing the website or
                using the Services, you agree to these Terms. If you do not
                agree, please do not use the Services.
              </p>

              {sections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="terms-section"
                >
                  <h2 className="text_accent fw-bold">{section.title}</h2>

                  <div
                    className={`terms-block ${section.highlight ? "highlight rounded" : ""}`}
                  >
                    {section.paragraphs?.map((p, j) => (
                      <p key={j} className="text-k-text">
                        {p}
                      </p>
                    ))}
                    {section.list && (
                      <ul className="text-k-text">
                        {section.list.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {section.after?.map((p, j) => (
                      <p key={j} className="text-k-text">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              {/* 18. Contact Us */}
              <section id={CONTACT_ID} className="terms-section mb-0">
                <h2 className="text_accent fw-bold">18. Contact Us</h2>
                <p className="text-k-text">
                  For questions, booking concerns, or complaints relating to
                  these Terms, contact Konnect Diagnostics:
                </p>

                <div className="contact-box rounded p-md-5 p-4 mt-4">
                  <img
                    src="/images/konnect-logo.png"
                    className="mb-3"
                    alt="konnect-diagnostics"
                  />
                  <p className="text-k-text">
                    <span className="fw-bold"> Registered Office: </span>
                    1-5-1055/87/236 & 237, HMT Officers Colony, Batukhamma Park,
                    Alwal, Hyderabad, Secunderabad, Telangana – 500010, India.
                  </p>
                  <p className="text-k-text">
                    <span className="fw-bold"> Email: </span>
                    <a href="mailto:info@konnectdiagnostics.com">
                      info@konnectdiagnostics.com
                    </a>
                  </p>
                  <p className="text-k-text">
                    <span className="fw-bold"> Customer Care No: </span>
                    <a href="tel:04041235555">040 - 4123 5555</a>
                  </p>
                  <p className="text-k-text mb-0">
                    <span className="fw-bold"> Website: </span>
                    <a href="https://konnectdiagnostics.com/">
                      https://konnectdiagnostics.com/
                    </a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

export default TermsAndConditions;

const Wrapper = styled.div`
  .toc {
    position: sticky;
    top: 110px;
    max-height: calc(100vh - 140px);
    overflow-y: auto;

    li a {
      display: block;
      padding: 7px 0 7px 12px;
      border-left: 2px solid transparent;
      color: #555;
      font-size: 14px;
      line-height: 1.4;
      text-decoration: none;
      transition:
        color 0.2s,
        border-color 0.2s;

      &:hover {
        color: #005ead;
      }

      &.active {
        color: #005ead;
        font-weight: 600;
        border-left-color: #005ead;
      }
    }
  }

  .terms-card {
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
  }

  .dates {
    font-size: 15px;
  }

  .intro {
    font-size: 17px;
    line-height: 1.8;
    padding-bottom: 28px;
    margin-bottom: 36px;
    border-bottom: 1px solid #e9ecef;
  }

  .terms-section {
    scroll-margin-top: 110px;
    margin-bottom: 40px;

    h2 {
      font-size: 24px;
      margin-bottom: 18px;
    }
  }

  .terms-block {
    p,
    li {
      line-height: 1.8;
    }

    ul {
      padding-left: 22px;
      margin-bottom: 12px;

      li {
        margin-bottom: 6px;
      }

      li::marker {
        color: #005ead;
      }
    }

    &.highlight {
      background-color: #eef5fc;
      border-left: 4px solid #005ead;
      padding: 18px 22px;

      p:last-child {
        margin-bottom: 0;
      }
    }
  }

  a {
    color: #005ead;
    text-decoration: none;
    word-break: break-word;

    &:hover {
      text-decoration: underline;
    }
  }

  .contact-box {
    background-image: url("/images/k-10.png");
    background-position: top center;
    background-size: cover;
    background-repeat: no-repeat;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;

    img {
      max-width: 260px;
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    .terms-section h2 {
      font-size: 20px;
    }

    .intro {
      font-size: 16px;
    }
  }
`;
